import React, { ReactElement, ReactNode, useMemo } from 'react';
import NextLink from 'next/link';
import { PrimaryHeader } from '@feast-it/ui/light';

import { FooterContainer } from '../footer';
import { coreNavigationFixture } from '../../constants/navigation';
import { ThemeWrapperUI } from '../../components/ThemeWrapperUI';
import { trackEvent } from '../../modules/analytics';
import { getAbsoluteUrl } from '../../helpers/formatUrl';
import {
  buildNavigationMetadataMap,
  getNavigationMetadata,
  NavigationMetadata,
} from '../../modules/navigationTracking';

interface LayoutProps {
  children: ReactNode;
}

interface CustomLinkProps {
  children: React.ReactNode;
  href?: string;
  navigationMetadataMap?: Map<string, NavigationMetadata>;
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent) => void;
  [key: string]: unknown;
}

const CustomLink = ({
  href,
  children,
  navigationMetadataMap,
  onClick,
  ...props
}: CustomLinkProps) => {
  const handleClick = (e: React.MouseEvent) => {
    // Track navigation click
    if (href && navigationMetadataMap) {
      const metadata = getNavigationMetadata(href, navigationMetadataMap);
      const currentPage =
        typeof window !== 'undefined' ? window.location.href : '';
      const destinationUrl = href.startsWith('http')
        ? href
        : getAbsoluteUrl(href);

      trackEvent('pressed_navigation_link', {
        currentPage,
        destinationUrl,
        header: metadata.header,
        subheader: metadata.subheader,
        type: metadata.type,
      });
    }

    // Call original onClick if provided
    if (onClick) {
      onClick(e);
    }
  };

  // If the link is a listing link, we cannot use the Next.js Link component
  // Using the Next.js Link component would log a failed to lookup route error
  if (href?.includes('listings')) {
    return (
      <a href={href} {...props} onClick={handleClick}>
        {children}
      </a>
    );
  }

  return (
    <NextLink
      href={href || '#'}
      prefetch={false}
      {...props}
      onClick={handleClick}
    >
      {children}
    </NextLink>
  );
};

export function Layout({ children }: LayoutProps): ReactElement {
  // Build navigation metadata map for tracking
  const navigationMetadataMap = useMemo(
    () => buildNavigationMetadataMap(coreNavigationFixture),
    []
  );

  // Create CustomLink with navigation metadata map
  const CustomLinkWithTracking = useMemo(() => {
    const TrackedLink = (linkProps: CustomLinkProps) => (
      <CustomLink
        {...linkProps}
        navigationMetadataMap={navigationMetadataMap}
      />
    );

    TrackedLink.displayName = 'TrackedLink';

    return TrackedLink;
  }, [navigationMetadataMap]);

  return (
    <>
      <ThemeWrapperUI>
        <PrimaryHeader
          customLinkComponent={CustomLinkWithTracking}
          brandVariant="logged_out_dark"
          coreNavigation={coreNavigationFixture}
        />

        {children}

        <FooterContainer />
      </ThemeWrapperUI>
    </>
  );
}
